import styled from '@emotion/styled';
import { css } from '@emotion/react';

import { colors, typography } from '../tokens';

export type TypographyVariant =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'body'
  | 'bodySmall'
  | 'label'
  | 'caption';

export type TypographySize =
  | 'xs'
  | 'sm'
  | 'md'
  | 'lg'
  | 'xl'
  | '2xl'
  | '3xl'
  | '4xl';
export type TypographyWeight = 'regular' | 'medium' | 'bold' | 'extraBold';

type StyledProps = {
  $variant: TypographyVariant;
  $size?: TypographySize;
  $weight?: TypographyWeight;
  $color?: string;
  $lineHeight?: number | string;
};

/**
 * Variant preset styles - maps typography variants to semantic styles
 */
const variantStyles: Record<TypographyVariant, ReturnType<typeof css>> = {
  h1: css`
    font-size: ${typography.fontSize['4xl']};
    font-weight: ${typography.fontWeight.extraBold};
    line-height: ${typography.lineHeight.tight};
    color: ${colors.textContrast};
  `,
  h2: css`
    font-size: ${typography.fontSize['3xl']};
    font-weight: ${typography.fontWeight.extraBold};
    line-height: ${typography.lineHeight.tight};
    color: ${colors.textContrast};
  `,
  h3: css`
    font-size: ${typography.fontSize.xl};
    font-weight: ${typography.fontWeight.bold};
    line-height: ${typography.lineHeight.normal};
    color: ${colors.textContrast};
  `,
  h4: css`
    font-size: ${typography.fontSize.lg};
    font-weight: ${typography.fontWeight.bold};
    line-height: ${typography.lineHeight.normal};
    color: ${colors.textContrast};
  `,
  h5: css`
    font-size: ${typography.fontSize.md};
    font-weight: ${typography.fontWeight.bold};
    line-height: ${typography.lineHeight.normal};
    color: ${colors.textContrast};
  `,
  h6: css`
    font-size: ${typography.fontSize.sm};
    font-weight: ${typography.fontWeight.bold};
    line-height: ${typography.lineHeight.normal};
    color: ${colors.textContrast};
  `,
  body: css`
    font-size: ${typography.fontSize.md};
    font-weight: ${typography.fontWeight.regular};
    line-height: ${typography.lineHeight.normal};
    color: ${colors.text};
  `,
  bodySmall: css`
    font-size: ${typography.fontSize.sm};
    font-weight: ${typography.fontWeight.regular};
    line-height: ${typography.lineHeight.normal};
    color: ${colors.text};
  `,
  label: css`
    font-size: ${typography.fontSize.sm};
    font-weight: ${typography.fontWeight.bold};
    line-height: ${typography.lineHeight.tight};
    color: ${colors.text};
    text-transform: uppercase;
    letter-spacing: 0.5px;
  `,
  caption: css`
    font-size: ${typography.fontSize.xs};
    font-weight: ${typography.fontWeight.regular};
    line-height: ${typography.lineHeight.normal};
    color: ${colors.textSecondary};
  `,
};

/**
 * Size overrides - allow customization of typography size
 */
const sizeOverrides: Record<TypographySize, ReturnType<typeof css>> = {
  xs: css`
    font-size: ${typography.fontSize.xs};
  `,
  sm: css`
    font-size: ${typography.fontSize.sm};
  `,
  md: css`
    font-size: ${typography.fontSize.md};
  `,
  lg: css`
    font-size: ${typography.fontSize.lg};
  `,
  xl: css`
    font-size: ${typography.fontSize.xl};
  `,
  '2xl': css`
    font-size: ${typography.fontSize['2xl']};
  `,
  '3xl': css`
    font-size: ${typography.fontSize['3xl']};
  `,
  '4xl': css`
    font-size: ${typography.fontSize['4xl']};
  `,
};

/**
 * Weight overrides - allow customization of font weight
 */
const weightOverrides: Record<TypographyWeight, ReturnType<typeof css>> = {
  regular: css`
    font-weight: ${typography.fontWeight.regular};
  `,
  medium: css`
    font-weight: ${typography.fontWeight.medium};
  `,
  bold: css`
    font-weight: ${typography.fontWeight.bold};
  `,
  extraBold: css`
    font-weight: ${typography.fontWeight.extraBold};
  `,
};

/**
 * Shared style block applied to every semantic element.
 *
 * Each semantic styled component below is created from a *real* HTML tag
 * (styled('h1'), styled('p'), ...) instead of wrapping a single <span> and
 * relying on the Emotion `as` prop. With the custom `shouldForwardProp` config
 * this Emotion build leaks the `as` prop to the DOM (rendering `<span as="h1">`),
 * which breaks semantic HTML and accessibility. Binding each component to its
 * tag keeps the rendered element deterministic and correct.
 */
const baseStyles = (props: StyledProps): ReturnType<typeof css> => css`
  font-family: ${typography.fontFamily.primary};
  margin: 0;
  padding: 0;

  ${variantStyles[props.$variant]}
  ${props.$size && sizeOverrides[props.$size]}
  ${props.$weight && weightOverrides[props.$weight]}
  ${props.$color &&
  css`
    color: ${props.$color};
  `}
  ${props.$lineHeight &&
  css`
    line-height: ${props.$lineHeight};
  `}
`;

const forwardConfig = {
  shouldForwardProp: (prop: string): boolean =>
    !prop.toString().startsWith('$'),
};

/**
 * Base styled component (renders as <span>).
 * Kept for backward compatibility and used as the fallback element.
 */
export const StyledTypography = styled('span', forwardConfig)<StyledProps>`
  ${(props) => baseStyles(props)}
`;

// Semantic styled components - each bound to its real HTML tag.
export const StyledH1 = styled('h1', forwardConfig)<StyledProps>`
  ${(props) => baseStyles(props)}
`;
export const StyledH2 = styled('h2', forwardConfig)<StyledProps>`
  ${(props) => baseStyles(props)}
`;
export const StyledH3 = styled('h3', forwardConfig)<StyledProps>`
  ${(props) => baseStyles(props)}
`;
export const StyledH4 = styled('h4', forwardConfig)<StyledProps>`
  ${(props) => baseStyles(props)}
`;
export const StyledH5 = styled('h5', forwardConfig)<StyledProps>`
  ${(props) => baseStyles(props)}
`;
export const StyledH6 = styled('h6', forwardConfig)<StyledProps>`
  ${(props) => baseStyles(props)}
`;
export const StyledP = styled('p', forwardConfig)<StyledProps>`
  ${(props) => baseStyles(props)}
`;
export const StyledLabel = styled('label', forwardConfig)<StyledProps>`
  ${(props) => baseStyles(props)}
`;
export const StyledDiv = styled('div', forwardConfig)<StyledProps>`
  ${(props) => baseStyles(props)}
`;
export const StyledSpan = StyledTypography;
